/**
 * ================================================================================================
 *  מרכז חיוב — THE CHARGES OF EVERY CUSTOMER, ON A SCREEN OF ITS OWN
 * ================================================================================================
 *
 * The Owner, 2026-11-02, twice, because I put it in the wrong place twice:
 *
 *     *"צריך להיות בקטגוריה נפרדת בבילינג ולא בבריאות המערכת — זה לא קשור לבריאות מערכת."*
 *     *"זה צריך להיות בנפרד ואל תכניס את הבילינג לתוך מנויים ומכסות, זה בנפרד."*
 *
 * It was inside the health screen, then inside מנויים ומכסות. Both were wrong for the same reason
 * and he had to say so twice: **a charge is not a subsystem and it is not a subscription.** Health
 * answers *"is this estate wired up"*, מנויים ומכסות answers *"what was sold and on what terms"*,
 * and this answers *"what moved, to whom, and did it work"* — three questions, three readers,
 * three urgencies. A failed charge is a customer to telephone today.
 *
 * ── THE DESTINATION ALREADY EXISTED, DECLARED AND EMPTY ───────────────────────────────────────
 *
 * `destinations.ts` carries `billing` — **מרכז חיוב**, under `platform.billing.history.read` —
 * marked `planned: true` with no screen behind it. So the separate category he asked for was
 * already the approved navigation, waiting for exactly this; building it anywhere else was
 * inventing a second home for something that had one.
 *
 * ── WHAT IS HERE, AND WHAT IS DELIBERATELY NOT ────────────────────────────────────────────────
 *
 *   · EVERY CUSTOMER'S CHARGES, each row naming the office, the period, the amount, the outcome,
 *     the document that came out of it and the provider's transaction id.
 *   · THE UNRESOLVED ONES FIRST, because they are the rows that need a person: a charge whose
 *     outcome at the provider is unknown leaves a period neither collected nor collectable.
 *   · THE TWO DOCUMENT STATES THE CUSTOMER'S OWN TAB NO LONGER SHOWS. They were taken off the
 *     office's screen the same day — *"אלו דברים שמעניינים אותי כמנהל פלטפורמה ולא כמשתמש קצה"* —
 *     and this is where they went.
 *
 * NO CREDIT BUTTON. *"זיכויים רק אני מפיק, לקוח קצה אינו רשאי להפיק זיכוי."* The authority is
 * his and the capability already encodes it with dual control; the MECHANISM is not built, and it
 * is not a wiring job — issuing a credit is raising a tax document and needs three decisions
 * nobody has taken. A button that refused would be worse than no button.
 */

import { useCallback } from 'react';
import type { JSX } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ServerRead } from '../components/ServerRead';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ClearingPanel } from './ClearingPanel';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';

export function PlatformChargesScreen(): JSX.Element {
  const ctx = useWorkContext();
  const { t } = useLocale();
  const read = useCallback(() => callOperation('platform.clearing.read', {}), []);
  const charges = useServerData(read);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={t('charges.title')} requires="PLATFORM" actual={ctx.surface} />;
  }

  return (
    <AppShell
      translated
      titleHe={t('charges.title')}
      backToParent={{ href: routes.platformConsole, labelHe: t('common.back') }}
    >
      <p className="page__lede">{t('charges.lede')}</p>

      <ServerRead result={charges.result} whatHe={t('charges.reading')} skeletonRows={5}>
        {(overview) => <ClearingPanel overview={overview} />}
      </ServerRead>

      {/*
        THE TWO SCREENS THAT ACT ON MONEY, REACHED FROM THE ONE THAT REPORTS ON IT. 2026-11-03.

        This screen answers *"is the clearing working"*; running the billing and chasing arrears
        are the two acts that follow from it, and neither had a way in from the console --
        `platformNavigation.test.ts` reported the collections address as one nothing leads to,
        which is what that control is for.
      */}
      <p className="actions-row">
        <a className="btn btn--nav" href={routes.platformBillingRun}>
          {t('charges.toBillingRun')}
        </a>
        <a className="btn btn--nav" href={routes.platformDunning}>
          {t('charges.toDunning')}
        </a>
        <a className="btn btn--nav" href={routes.platformChangeRequests}>
          {t('charges.toChangeRequests')}
        </a>
      </p>
    </AppShell>
  );
}
